import { useParams } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Plus, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useEntnahmeReasons,
  useSaveEntnahmeReasons,
} from '../hooks/use-entnahme-reasons'
import type { EntnahmeReasonInput } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PermissionGate } from '@/shared/components/permission-gate'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

type EntnahmeReasonRow = {
  code: string
  label: string
  active: boolean
  sortOrder: number
}

const EMPTY_ROW: Omit<EntnahmeReasonRow, 'code'> = {
  label: '',
  active: true,
  sortOrder: 0,
}

function getNextCode(rows: Array<EntnahmeReasonRow>) {
  const used = new Set(rows.map((row) => row.code).filter(Boolean))
  let max = 0
  used.forEach((code) => {
    const value = Number(code)
    if (Number.isInteger(value) && value > max) {
      max = value
    }
  })
  let next = max + 1
  while (used.has(String(next))) {
    next += 1
  }
  return String(next)
}

function buildRow(
  rows: Array<EntnahmeReasonRow>,
  base: Omit<EntnahmeReasonRow, 'code'>,
) {
  return {
    code: getNextCode(rows),
    ...base,
  }
}

export function RestaurantEntnahmeReasonsPage() {
  const { t: tRestaurants } = useTranslation('restaurants')
  const { t: tCommon } = useTranslation('common')
  const { id } = useParams({ strict: false })
  const restaurantId = Number(id)
  const { data, isLoading, error } = useEntnahmeReasons(restaurantId)
  const saveMutation = useSaveEntnahmeReasons(restaurantId)
  const { can } = usePermissions()
  const canManage = can(ACTIONS.ENTNAHME_REASON_MANAGE)
  const [rows, setRows] = useState<Array<EntnahmeReasonRow>>([])

  useEffect(() => {
    if (!data) return
    const next = data.items.map((item) => ({
      code: item.code,
      label: item.label,
      active: item.active ?? true,
      sortOrder: item.sortOrder ?? 0,
    }))
    setRows(next.length > 0 ? next : [buildRow(next, EMPTY_ROW)])
  }, [data])

  const missingRestaurant = useMemo(
    () => !Number.isFinite(restaurantId) || restaurantId <= 0,
    [restaurantId],
  )

  function updateRow(index: number, patch: Partial<EntnahmeReasonRow>) {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    )
  }

  function addRow() {
    setRows((prev) => [...prev, buildRow(prev, EMPTY_ROW)])
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index))
  }

  function assignMissingCodes(current: Array<EntnahmeReasonRow>) {
    const out = [...current]
    let nextCode = getNextCode(out)
    for (let i = 0; i < out.length; i += 1) {
      if (!out[i].code) {
        out[i] = { ...out[i], code: nextCode }
        nextCode = getNextCode(out)
      }
    }
    return out
  }

  function handleSave() {
    if (missingRestaurant) {
      toast.error(tRestaurants('entnahmeReasons.toasts.missingRestaurant'))
      return
    }
    const withCodes = assignMissingCodes(rows)
    setRows(withCodes)
    const items: Array<EntnahmeReasonInput> = []
    const seenCodes = new Set<string>()
    for (const row of withCodes) {
      const code = row.code.trim()
      const label = row.label.trim()
      if (!label) {
        toast.error(tRestaurants('entnahmeReasons.toasts.labelRequired'))
        return
      }
      if (seenCodes.has(code)) {
        toast.error(
          tRestaurants('entnahmeReasons.toasts.duplicateCode', { code }),
        )
        return
      }
      seenCodes.add(code)
      items.push({
        code,
        label,
        active: row.active,
        sortOrder: row.sortOrder,
      })
    }
    saveMutation.mutate(items)
  }

  return (
    <PermissionGate action={ACTIONS.ENTNAHME_REASON_VIEW}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              {tRestaurants('entnahmeReasons.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {tRestaurants('entnahmeReasons.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={addRow} disabled={!canManage}>
              <Plus className="mr-2 h-4 w-4" />
              {tRestaurants('entnahmeReasons.add')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saveMutation.isPending || !canManage}
            >
              <Save className="mr-2 h-4 w-4" />
              {saveMutation.isPending ? tCommon('saving') : tCommon('save')}
            </Button>
          </div>
        </div>

        {missingRestaurant && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {tRestaurants('entnahmeReasons.missingRestaurantTitle')}
            </AlertTitle>
            <AlertDescription>
              {tRestaurants('entnahmeReasons.missingRestaurantDescription')}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {tRestaurants('entnahmeReasons.loadErrorTitle')}
            </AlertTitle>
            <AlertDescription>
              {tRestaurants('entnahmeReasons.loadErrorDescription')}
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-sm text-muted-foreground">
                {tRestaurants('entnahmeReasons.loading')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px]">
                      {tRestaurants('entnahmeReasons.table.id')}
                    </TableHead>
                    <TableHead>
                      {tRestaurants('entnahmeReasons.table.label')}
                    </TableHead>
                    <TableHead className="w-[120px]">
                      {tRestaurants('entnahmeReasons.table.sort')}
                    </TableHead>
                    <TableHead className="w-[120px]">
                      {tRestaurants('entnahmeReasons.table.active')}
                    </TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.code}-${index}`}>
                      <TableCell>
                        <span className="font-mono text-sm">
                          {row.code ||
                            tRestaurants('entnahmeReasons.table.auto')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.label}
                          onChange={(e) =>
                            updateRow(index, { label: e.target.value })
                          }
                          placeholder={tRestaurants(
                            'entnahmeReasons.table.placeholder',
                          )}
                          disabled={!canManage}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={row.sortOrder}
                          onChange={(e) =>
                            updateRow(index, {
                              sortOrder: Number(e.target.value) || 0,
                            })
                          }
                          disabled={!canManage}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={row.active}
                            onCheckedChange={(value) =>
                              updateRow(index, { active: Boolean(value) })
                            }
                            disabled={!canManage}
                          />
                          <span className="text-sm text-muted-foreground">
                            {row.active
                              ? tCommon('active')
                              : tCommon('inactive')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(index)}
                          aria-label={tRestaurants(
                            'entnahmeReasons.table.removeAria',
                          )}
                          disabled={!canManage}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="text-sm text-muted-foreground"
                      >
                        {tRestaurants('entnahmeReasons.table.empty')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  )
}
